import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Coins } from "lucide-react";
import api from "../api/client";
import { AdminTabs } from "./AdminDashboard";
import { formatNumber } from "../lib/format";

export default function AdminFeesSwap() {
    const { swapId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/admin/fees/swap/${swapId}`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message));
    }, [swapId]);

    if (err) {
        return (
            <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
                <div className="max-w-[1080px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                    <AdminTabs />
                    <div className="text-center py-20 text-zinc-500 font-mono text-xs">{err}</div>
                </div>
            </div>
        );
    }
    if (!data) {
        return <div className="min-h-[60vh] grid place-items-center font-mono text-xs text-zinc-500">Loading swap fees…</div>;
    }

    const t = data.totals;
    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1080px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <Link to="/admin/fees" className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 hover:text-cyan-300"
                          data-testid="admin-fees-back">
                        <ArrowLeft className="w-3 h-3" /> Back to fees ledger
                    </Link>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mt-3">Admin · Fee details for swap</div>
                    <h1 className="text-2xl font-medium tracking-tighter mt-2 font-mono">{data.swap_id}</h1>
                </div>
                <AdminTabs />

                {/* Trade meta */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    <Meta label="Pair" value={data.pair || data.coin} />
                    <Meta label="Duration" value={data.duration_key || "—"} />
                    <Meta label="Notional" value={`$${formatNumber(data.notional)}`} />
                    <Meta label="State / Outcome" value={`${data.state || "—"} · ${data.outcome || "—"}`} />
                </div>

                {/* Fee totals for this single swap */}
                <div className="bg-[#0A0A0C] border border-cyan-500/30 rounded-sm p-5" data-testid="admin-fees-swap-totals">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">
                        <Coins className="w-3 h-3" /> Fees collected on this swap
                    </div>
                    <div className="grid sm:grid-cols-4 gap-2 mt-4">
                        <Tile label="Entry total" value={`$${formatNumber(t.entry)}`}
                              sub={`quoted ${data.entry_fee_quoted ?? 0} per side · 2 sides`} />
                        <Tile label="Settlement total" value={`$${formatNumber(t.settlement)}`}
                              sub={`quoted ${data.settlement_fee_quoted ?? 0} per side · 2 sides`} />
                        <Tile label="Liquidation total" value={`$${formatNumber(t.liquidation)}`}
                              sub="taken from loser margin" accent="text-rose-300" />
                        <Tile label="Grand total" value={`$${formatNumber(t.grand_total)}`}
                              accent="text-cyan-300" />
                    </div>
                </div>

                {/* Events */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-[#1F1F24]">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Events</div>
                        <div className="text-sm font-medium mt-1">{data.events.length} fee event(s)</div>
                    </div>
                    <table className="w-full text-[12px] font-mono">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                <th className="text-left py-2 px-4">Time (UTC)</th>
                                <th className="text-left py-2 px-4">Type</th>
                                <th className="text-left py-2 px-4">User</th>
                                <th className="text-right py-2 px-4">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.events.map((e, i) => (
                                <tr key={i} className="border-b border-[#1F1F24]/60">
                                    <td className="py-2 px-4 text-zinc-400 tabular-nums">{(e.created_at || "").replace("T", " ").slice(0, 19)}</td>
                                    <td className="py-2 px-4 text-zinc-100">{e.type}</td>
                                    <td className="py-2 px-4 text-zinc-500">{e.user_id ? `${e.user_id.slice(0, 8)}…` : "—"}</td>
                                    <td className="py-2 px-4 text-right tabular-nums text-cyan-300">
                                        ${formatNumber(e.amount ?? (e.fee || 0) + (e.liquidation_fee || 0))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Tile({ label, value, sub, accent = "text-zinc-100" }) {
    return (
        <div className="border border-[#1F1F24] rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className={`text-xl font-medium tabular-nums mt-1 ${accent}`}>{value}</div>
            {sub && <div className="text-[9.5px] font-mono text-zinc-600 mt-1 leading-relaxed">{sub}</div>}
        </div>
    );
}
function Meta({ label, value }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <div className="font-mono text-sm text-zinc-100 mt-1">{value}</div>
        </div>
    );
}
